import React from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { FormControlLabel, FormGroup } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import classNames from "classnames";
import InputAdornment from "@material-ui/core/InputAdornment";
import FormHelperText from "@material-ui/core/FormHelperText";
import { useGameContextContext } from "../../../state/game-context/context";
import DateFormat from "../../../util/date-format";
import { useFootballContext } from "../../../state/football-play/context";
import { useEventContext } from "../../../state/events/context";
import timePickerStyles from "../../common/styles/TimePicker.styles";
import { useAppContext } from "../../../state/app/context";

const toMillis = (minutes, seconds) => {
	return (minutes * 60 + seconds) * 1000;
};

const EventTimePicker = ({
	period,
	seconds,
	onChange,
	label,
	autoFocus,
	disableValidation,
	showHours,
	onValidate = () => {},
	hideErrors,
	disabled = false
}) => {
	const classes = timePickerStyles();

	const { isValidTime } = useFootballContext();

	const { state: appState } = useAppContext();
	const isEditPlay = !!appState.editPlay()?.index;
	const { state: gameContextState } = useGameContextContext();
	const fbOtDisabled = gameContextState.fbOtDisabled();
	const fbOtDisabledText = "--";
	const isDisabled = fbOtDisabled || disabled;
	const { state } = useEventContext();
	const maxMinutes = state.maxMinutesByPeriod(period);
	const maxSeconds = 59;
	const maxHours = showHours ? 12 : 0;
	const [h, m, s] = DateFormat.millisecondsToArray(seconds * 1000);

	const validationSchema = Yup.object({
		hours: Yup.number()
			.typeError("Invalid")
			.required("Required")
			.moreThan(-1, "Invalid")
			.integer("Invalid")
			.max(maxHours, `Max: ${maxHours}`),
		minutes: Yup.number()
			.typeError("Invalid")
			.required("Required")
			.moreThan(-1, "Invalid")
			.integer("Invalid")
			.max(maxMinutes, `Max: ${maxMinutes}`),
		seconds: Yup.number()
			.typeError("Invalid")
			.required("Required")
			.moreThan(-1, "Invalid")
			.integer("Invalid")
			.max(maxSeconds, `Max: ${maxSeconds}`)
	});

	const { errors, touched, setFieldValue, values } = useFormik({
		enableReinitialize: true,
		initialValues: {
			hours: fbOtDisabled ? fbOtDisabledText : h.toString().padStart(2, "0"),
			minutes: fbOtDisabled ? fbOtDisabledText : m.toString().padStart(2, "0"),
			seconds: fbOtDisabled ? fbOtDisabledText : s.toString().padStart(2, "0")
		},
		validationSchema,
		validate: ({ minutes, seconds: currentSeconds }) => {
			const validationErrors = {};

			if (Number(minutes) === maxMinutes && Number(currentSeconds) > 0) {
				errors.general = `Max: ${maxMinutes}:00`;
			}

			const millis = toMillis(Number(minutes), Number(currentSeconds));
			if (!disableValidation && !isValidTime(period, millis)) {
				validationErrors.general = isEditPlay
					? "Enter a time at or before the next timed play and at or after the previous play."
					: "Please enter a time that is after that last play.";
			}

			onValidate(!Object.entries(validationErrors).length, validationErrors);

			return errors;
		}
	});

	const generateOptions = (max) => {
		const options = [];
		for (let i = 0; i <= max; i += 1) {
			options.push(i.toString().padStart(2, "0"));
		}

		return options;
	};

	const handleChange = (field, value) => {
		if (value !== undefined) {
			setFieldValue(field, value.padStart(2, "0"));

			let totalSeconds;
			const currentHours = Number(values.hours);
			const currentMinutes = Number(values.minutes);
			const currentSeconds = Number(values.seconds);
			const newValue = Number(value);

			if (field === "hours") {
				totalSeconds = newValue + currentMinutes * 60 + currentSeconds;
			} else if (field === "minutes") {
				totalSeconds = currentHours * 3600 + newValue * 60 + currentSeconds;
			} else {
				totalSeconds = currentHours * 3600 + currentMinutes * 60 + newValue;
			}

			onChange(totalSeconds);
		}
	};

	const handleBlur = (field, value) => {
		handleChange(field, value);
	};

	const hoursOptions = generateOptions(maxHours);
	const minuteOptions = generateOptions(maxMinutes);
	const secondOptions = generateOptions(maxSeconds);

	const secondsRef = React.useRef();
	const minutesRef = React.useRef();

	return (
		<FormGroup>
			<FormControlLabel
				labelPlacement="top"
				label={label}
				classes={{
					label: classes.label,
					labelPlacementTop: classes.labelPlacementTop
				}}
				control={
					<Box display="flex" className={classes.flexColumn}>
						<Box display="flex" className={classes.justifyCenter}>
							{maxHours > 0 && (
								<>
									<Autocomplete
										id="hours"
										classes={{ inputRoot: classes.input }}
										className={classes.autocomplete}
										options={hoursOptions}
										filterOptions={(options) =>
											options.filter((o) => hoursOptions.includes(o))
										}
										noOptionsText="-"
										disableClearable
										autoSelect
										disabled={isDisabled}
										renderInput={(params) => (
											<TextField
												{...params}
												autoFocus={autoFocus}
												helperText={touched.hours ? errors.hours : ""}
												error={touched.hours && Boolean(errors.hours)}
												className={classNames({ [classes.dashedInput]: isDisabled })}
												InputLabelProps={{
													className: classes.textOverflow
												}}
												InputProps={{
													...params.InputProps,
													endAdornment: (
														<InputAdornment position="end" className={classes.endAdornment}>
															h
														</InputAdornment>
													),
													inputMode: "numeric"
												}}
												/* eslint-disable-next-line react/jsx-no-duplicate-props */
												inputProps={{
													...params.inputProps,
													maxLength: 2
												}}
											/>
										)}
										value={values.hours}
										onInputChange={(e) => {
											if (e?.target?.value?.length === 2) {
												handleChange("hours", e?.target.value);
												minutesRef.current.focus();
												minutesRef.current.setSelectionRange(0, 2);
											}
										}}
										onBlur={(e) => handleBlur("hours", e.target.value)}
										onChange={(e, value) => handleChange("hours", value)}
									/>
									<span className={classes.separator}>:</span>
								</>
							)}
							<Autocomplete
								id="minutes"
								name="minutes"
								classes={{ inputRoot: classes.input }}
								className={classes.autocomplete}
								options={minuteOptions}
								disableClearable
								autoSelect
								disabled={isDisabled}
								renderInput={(params) => (
									<TextField
										{...params}
										autoFocus={autoFocus}
										helperText={!hideErrors && touched.minutes ? errors.minutes : ""}
										error={touched.minutes && Boolean(errors.minutes)}
										className={classNames({ [classes.dashedInput]: isDisabled })}
										InputLabelProps={{
											className: classes.textOverflow
										}}
										InputProps={{
											...params.InputProps,
											endAdornment: (
												<InputAdornment position="end" className={classes.endAdornment}>
													m
												</InputAdornment>
											),
											inputMode: "numeric"
										}}
										/* eslint-disable-next-line react/jsx-no-duplicate-props */
										inputProps={{
											...params.inputProps,
											maxLength: 2
										}}
										inputRef={minutesRef}
									/>
								)}
								value={values.minutes}
								onInputChange={(e) => {
									if (e?.target?.value?.length === 2) {
										handleChange("minutes", e?.target.value);
										secondsRef.current.focus();
										secondsRef.current.setSelectionRange(0, 2);
									}
								}}
								onBlur={(e) => handleBlur("minutes", e.target.value)}
								onChange={(e, value) => handleChange("minutes", value)}
								onFocus={(e) => {
									// TODO: We need to call persist due to React16 event pooling issue. https://legacy.reactjs.org/docs/legacy-event-pooling.html
									e.persist();
									setTimeout(() => e.target.setSelectionRange(0, 2));
								}}
							/>
							<span className={classes.separator}>:</span>
							<Autocomplete
								id="seconds"
								name="seconds"
								classes={{ inputRoot: classes.input }}
								className={classes.autocomplete}
								options={secondOptions}
								disableClearable
								autoSelect
								disabled={isDisabled}
								renderInput={(params) => (
									<TextField
										{...params}
										helperText={!hideErrors && touched.seconds ? errors.seconds : ""}
										error={touched.seconds && Boolean(errors.seconds)}
										className={classNames({ [classes.dashedInput]: isDisabled })}
										InputProps={{
											...params.InputProps,
											endAdornment: (
												<InputAdornment position="end" className={classes.endAdornment}>
													s
												</InputAdornment>
											),
											inputMode: "numeric"
										}}
										/* eslint-disable-next-line react/jsx-no-duplicate-props */
										inputProps={{
											...params.inputProps,
											maxLength: 2
										}}
										inputRef={secondsRef}
									/>
								)}
								value={values.seconds}
								onInputChange={(e) => {
									if (e?.target?.value?.length === 2) {
										handleChange("seconds", e?.target.value);
									}
								}}
								onBlur={(e) => handleBlur("seconds", e.target.value)}
								onChange={(e, value) => handleChange("seconds", value)}
							/>
						</Box>
						{!hideErrors && (
							<Box>
								<FormHelperText
									error={Boolean(errors.general)}
									className={classes.errorMessage}
								>
									{errors.general}
								</FormHelperText>
							</Box>
						)}
					</Box>
				}
			/>
		</FormGroup>
	);
};

export default EventTimePicker;
